import React, { Component } from 'react'
import { observable, toJS } from 'mobx'
import { observer, inject } from 'mobx-react'
import { Link } from 'react-router'

import Collapsible from '../../util/Collapsible'
import Modal from "../../util/Modal"
import TeamsStore from '../../../stores/teams'
import ResellerTeamsStore from '../../../stores/reseller_teams'
import WebsitesStore from '../../../stores/websites'
import MlsStore from '../../../stores/mls'

import Icon from '../../util/Icon'
import StoreAutoComplete from '../../util/auto_complete/StoreAutoComplete'
import MlsAutocomplete from '../../util/auto_complete/MlsAutocomplete'

import range from 'lodash/range'
import uniqBy from 'lodash/uniqBy'

@inject('auth')
@observer
export default class TeamDetails extends Component {
  @observable legacyUserAgreements = ""

  state = {
    trialDays: 10,
    isEditingResellerTeam: false,
    onboardingOpen: false,
    onboardingText: '',
    onboardingName: '',
    editingOfficeName: false
  }

  teams = new TeamsStore()

  resellerTeams = new ResellerTeamsStore()

  user = this.props.auth.user

  /*
   * Determine whether the current user is a reseller admin or broker
   */
  isUserAdminOrBroker = () =>  this.user.admin || this.user.broker

  /*
   * Show and hide the onboarding instructions popup
   */
  showOI = (mls) => this.setState({ 
    onboardingName: mls.mls_id.toUpperCase(), 
    onboardingText: mls.onboarding_instructions || '', 
    onboardingOpen: true 
  })
  hideOI = () => this.setState({ onboardingOpen: false })

  /*
   * Gather the appropriate MLSs that a team should have onboarding
   * instructions buttons for.
   * 
   * These MLSs comprise the distinct union of:
   *  - MLSs that a team is provisioned for at order time
   *  - MLSs that a team has permissions for (via the websites page)
   * 
   * MLSs without onboarding instructions and the demo MLS
   * are not included.
   */
  gatherOIs = (team) => {
    const provisionedMlses = this.getProvisionedMlses(team.order)
    const permittedMlses = this.getPermittedMlses(team)
    const mlses = provisionedMlses.concat(permittedMlses)
    const uniqueMlses = uniqBy(mlses, 'mls_id')
    return uniqueMlses
  }
  
  /*
   * Get the MLSs that a team is provisioned for at order time.
   */
  getProvisionedMlses = (order) => {
    const mlsIds = this.getMlsIdsFromOrder(order)
    const mlses = this.mlsStore.mlsList.filter(mls => {
      return mlsIds.includes(mls.mls_id)
    })
    return mlses
  }
  
  /*
   * Get the MLS IDs from a team's order.
   * The MLS IDs are in two different places inside the order object,
   * which may or may not match. To be thorough, we are gathering from
   * both places.
   */
  getMlsIdsFromOrder = (order) => {
    const orderBlob = toJS(order)
    let mlsIds = []
    if (!!orderBlob && orderBlob.mls) {
      mlsIds = mlsIds.concat(Object.keys(orderBlob.mls))
    }
    if (!!orderBlob && orderBlob.provision && orderBlob.provision.mls) {
      mlsIds = mlsIds.concat(Object.keys(orderBlob.provision.mls))
    }
    return mlsIds
  }
  
  /*
   * Get the MLSs that a team has permissions for (via the websites page).
   * We are excluding MLSs without onboarding instructions and the demo MLS.
   */
  getPermittedMlses = (team) => {

    return (team.mls || []).filter(mls => {
      return !!mls.onboarding_instructions && mls.mls_id !== 'demo'
    })
  }

  componentWillMount() {
    const { params: { team_id } } = this.props

    this.loadTeam(team_id)
    this.teams.loadUsers(team_id)

    this.websites = new WebsitesStore({ team: { id: team_id } })
    this.websites.load()
    
    this.mlsStore = new MlsStore()
    this.mlsStore.loadMlsInfo()
  }

  loadTeam(team_id) {
    this.teams.load(team_id).then(() => {
      const team = this.teams.findById(team_id)

      this.legacyUserAgreements = team.legacy_user_agreements.join("\n")

      if (team.license && team.license.trial_ends_at) {
        const trialEndsAt = new Date(team.license.trial_ends_at)
        const trialEndYear = trialEndsAt.getFullYear()
        const trialEndMonth = trialEndsAt.getMonth()
        const trialEndDay = trialEndsAt.getDate()
        this.setState({
          trialEndDay,
          trialEndMonth,
          trialEndYear,
        })
      }
    })
  }

  activateLicense = event => {
    event.preventDefault()

    const { params: { team_id } } = this.props
    const { trialDays } = this.state
    const team = this.teams.findById(team_id)

    if (team.reseller_direct_bill) {
      this.teams.activateDirectBillLicense(team_id, team.plan, trialDays).then(license => team.license = license)
    } else {
      // start trial
      this.teams.startTrial(team_id, team.plan, trialDays).then(license => team.license = license)
    }
  }

  updatePlan = event => {
    event.preventDefault()
    const form = event.target
    const planTemplate = form.elements.namedItem('planTemplate').value
    const rate = form.elements.namedItem('rate').value
    const plan = planTemplate.replace('[rate]', rate)

    const { params: { team_id } } = this.props
    this.teams.updateLicense(team_id, { plan }).then(() => {
      this.setState({ editingPlan: false })
    })
  }

  revoke = event => {
    event.preventDefault()
    const { params: { team_id } } = this.props
    this.teams.updateLicense(team_id, { revoked: true })
  }

  unrevoke = event => {
    event.preventDefault()
    const { params: { team_id } } = this.props
    this.teams.updateLicense(team_id, { revoked: false })
  }

  impersonateUser = ({ impersonate_token }) => event => {
    event.preventDefault()

    const form = document.createElement('form')
    const token = document.createElement('input')

    form.method = 'POST'
    form.action = `${process.env.ADMIN_URL}/api/auth/impersonate`

    token.name = 'token'
    token.value = impersonate_token

    form.appendChild(token)
    document.body.appendChild(form)

    form.submit()
  }

  handleEditResellerTeam = event => {
    event.preventDefault()
    this.resellerTeams.load()
    this.setState({ isEditingResellerTeam: true })
  }

  handleSelectResellerTeam = team => {
    this.setState({ selectedResellerTeam: team })
  }

  handleSaveResellerTeam = event => {
    event.preventDefault()
    const { selectedResellerTeam } = this.state
    if (!selectedResellerTeam) {
      alert('Please select a reseller team')
      return
    }
    const { params: { team_id } } = this.props
    const team = this.teams.findById(team_id)
    team.reseller_team_name = selectedResellerTeam.name
    team.reseller_team_id = selectedResellerTeam.id

    this.teams.updateResellerTeam(team_id, selectedResellerTeam.id).then(() => {
      this.setState({
        isEditingResellerTeam: false,
        selectedResellerTeam: null,
      })
      this.loadTeam(team_id)
    })
  }

  handleCancelResellerTeam = event => {
    event.preventDefault()
    this.setState({
      isEditingResellerTeam: false,
      selectedResellerTeam: null,
    })
  }

  renderResellerTeam() {
    if (this.props.auth.user.admin) {
      const { params: { team_id } } = this.props
      const team = this.teams.findById(team_id)

      const { isEditingResellerTeam } = this.state

      const editResellerTeamButton = isEditingResellerTeam ? (
        <span>
          <button onClick={this.handleSaveResellerTeam}>Save</button>
          {' '}
          <button onClick={this.handleCancelResellerTeam}>Cancel</button>
        </span>
      ) : (
        <button onClick={this.handleEditResellerTeam}>Edit</button>
      )

      const resellerTeamLink = isEditingResellerTeam ? (
        <StoreAutoComplete
          placeholder="Choose A Reseller Team"
          onSelect={this.handleSelectResellerTeam}
          store={this.resellerTeams} />
      ) : (
        <Link to={`/admin/resellers/teams/${team.reseller_team_id}`}>{team.reseller_team_name}</Link >
      )

      return (
        <li><strong>Reseller Team:</strong> {resellerTeamLink} [{team.reseller_direct_bill ? 'Direct Bill' : 'Stripe-Based'}] {editResellerTeamButton}</li>
      )
    }
  }

  renderPlanName(license) {
    if (!license) return

    return {
      basic_monthly: 'Basic Monthly (legacy)',
      basic_yearly: 'Basic Yearly (legacy)',
      premium_monthly: 'Premium Monthly (legacy)',
      premium_yearly: 'Premium Yearly (legacy)',
      essentials_monthly: 'Essentials Monthly (legacy)',
      essentials_yearly: 'Essentials Yearly (legacy)',
      new_premium_monthly: 'Premium Monthly (legacy)',
      new_premium_yearly: 'Premium Yearly (legacy)',
      essentials_monthly_APR24: 'Essentials Monthly (legacy)',
      essentials_yearly_APR24: 'Essentials Yearly (legacy)',
      premium_monthly_APR26: 'Premium Monthly',
      premium_yearly_APR26: 'Premium Yearly',
      essentials_monthly_APR26: 'Essentials Monthly',
      essentials_yearly_APR26: 'Essentials Yearly',
    }[license.full_plan_name]
  }

  /*
   * Show the team's plan and the plan edit form
   */
  renderPlan() {
    let contents
    const { params: { team_id } } = this.props
    const team = this.teams.findById(team_id)
    let rate
    let planTemplate

    if (team && team.license) {
      rate = team.license && team.license.rate
      planTemplate = team.license.full_plan_name.replace('monthly', '[rate]').replace('yearly', '[rate]')
    }

    const Cap = ({ children }) => <span style={{textTransform: 'capitalize'}}>{children}</span>
    if (this.state.editingPlan) {
      contents = <form onSubmit={this.updatePlan} style={{display: 'inline-flex', alignItems: 'center'}}>
        <select name="planTemplate" defaultValue={planTemplate}>
          {planTemplate == "basic_[rate]" && <option value="basic_[rate]">Basic (legacy)</option>}
          {planTemplate == "premium_[rate]" && <option value="premium_[rate]">Premium (legacy)</option>}
          {planTemplate == "essentials_[rate]" && <option value="essentials_[rate]">Essentials (legacy)</option>}
          {planTemplate == "essentials_[rate]_APR24" && <option value="essentials_[rate]_APR24">Essentials (legacy)</option>}
          {planTemplate == "new_premium_[rate]" && <option value="new_premium_[rate]">Premium (legacy)</option>}

          <option value="essentials_[rate]_APR26">Essentials</option>
          <option value="premium_[rate]_APR26">Premium</option>
        </select>
        <select name="rate" defaultValue={rate} style={{marginLeft: '5px'}}>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
        </select>
        <button style={{margin: 0, marginLeft: '5px'}}>Save</button>
        <a href="#" style={{marginLeft: '5px'}} onClick={event => {
          event.preventDefault()
          this.setState({ editingPlan: false })
        }}>Cancel</a>
      </form>
    } else {
      contents = <span>
        <Cap>{team && this.renderPlanName(team.license)}</Cap> <a href="#" onClick={event => {
          event.preventDefault()
          this.setState({ editingPlan: true })
        }}>Edit</a>
      </span>
    }
    return (
      <li><strong>Plan:</strong> {contents}</li>
    )
  }

  handleLegacyUserAgreementsChange(e) {
    this.legacyUserAgreements = e.target.value
  }

  handleSaveLegacyUserAgreements = event => {
    event.preventDefault()
    const { params: { team_id } } = this.props
    const team = this.teams.findById(team_id)
    team.legacy_user_agreements = this.legacyUserAgreements.split("\n")
    this.teams.update(team).then(() => {
      this.loadTeam(team_id)
    })
  }

  renderLegacyPaperwork() {
    // only reseller admins (sidx folks) should see this
    if (!this.props.auth.user.admin) {
      return
    }

    return (<div className="panel half padded">
      <h4>Legacy Paperwork</h4>
        <form>
          <textarea name="legacyUserAgreements" value={this.legacyUserAgreements} onChange={(e) => this.handleLegacyUserAgreementsChange(e)} />
          <button className="button" onClick={(e) => this.handleSaveLegacyUserAgreements(e)}>Save</button>
        </form>
      </div>
    )
  }

  renderLicenseStatus() {
    const { params: { team_id } } = this.props
    const team = this.teams.findById(team_id)
    let unsubscribed = ''
    const trialMessage = dts => {
      if (!dts) return 'No Trial'
      const d = new Date(dts)
      const trial_ends = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`
      const ends_ended = d > new Date() ? 'Ends' : 'Ended'
      return `Trial ${ends_ended} On ${trial_ends}`
    }
    if (team.license) {
      if (team.reseller_direct_bill === false && !team.license.stripe_subscription_id) {
        unsubscribed = `(Unsubscribed - ${trialMessage(team.license.trial_ends_at)})`
      }
      return (
        <li><strong>Status:</strong> {team.license.status} {unsubscribed}</li>
      )
    } else {
      return (
        <li><strong>Status:</strong> New</li>
      )
    }
  }

  /*
   * A collection of buttons for popping up onboarding instructions,
   * as well as a search form to find onboarding instructions for any MLS.
   * The buttons comprise the distinct union of:
   *  - MLSs that a team has permissions for (via the websites page)
   *  - MLSs that a team is provisioned for at order time
   * MLSs without onboarding instructions and the demo MLS
   * are not included.
   */
  renderOnboardingSelections() {
    const { params: { team_id } } = this.props
    const team = this.teams.findById(team_id)
    const { onboardingOpen, onboardingName, onboardingText } = this.state

    if (!team) return null

    return (
      <div>
        <fieldset className="onboarding">
          <legend>Onboarding Instructions</legend>
          {this.gatherOIs(team).map((mls, index) => (
            <button key={index} className="button" onClick={() => this.showOI(mls)}>{mls.mls_id.toUpperCase()}</button>
          ))}
          <MlsAutocomplete mlsStore={this.mlsStore} onSelect={this.handleSelectMls}/>
        </fieldset>
        <Modal isOpen={onboardingOpen} onRequestClose={this.hideOI}>
          <div className="onboarding-instructions">
            <h2>Onboarding Instructions for {onboardingName}</h2>
            <pre>
              {onboardingText}
            </pre>
          </div>
        </Modal>
      </div>
    )
  }
  
  /*
   * Pop up onboarding instructions for a selected MLS.
   * Don't try to open the modal when the MLS selection is cleared.
   */
  handleSelectMls = (option) => {
    if (!!option) this.showOI(option) // eslint-disable-line no-extra-boolean-cast
  }

  /*
   * Show the form to start a trial or activate a license for a team
   */
  renderStartTrial() {
    if (!this.isUserAdminOrBroker()) return

    const { params: { team_id } } = this.props
    const { trialDays } = this.state
    const team = this.teams.findById(team_id)

    if (!team.license) {
      if (team.reseller_direct_bill) {
        return (
          <form className="trial" onSubmit={this.activateLicense}>
            <button className="button good" disabled={team.license}>Activate</button>
          </form>
        )
      } else {
        return (
          <form className="trial" onSubmit={this.activateLicense}>
            <button className="button good" disabled={team.license}>Start Trial</button>

            <label htmlFor="days">Days:</label>
            <input type="number" id="days" value={trialDays} onChange={e => this.setState({ trialDays: e.target.value })} />
          </form>
        )
      }
    }
  }

  /*
   * Show the revoke/reinstate license button
   */
  renderRevokeButton() {
    if (!this.isUserAdminOrBroker()) return

    const { params: { team_id } } = this.props
    const team = this.teams.findById(team_id)
    if (team.license && team.license.revoked) {
      return (
        <a className="button good" onClick={this.unrevoke}>Reinstate License</a>
      )
    } else if (team.license) {
      return (
        <a className="button danger" onClick={this.revoke}>Revoke License</a>
      )
    }
  }

  handleEditTrial = () => {
    const { trialEndDay, trialEndMonth, trialEndYear } = this.state
    const newTrialEndDate = [parseInt(trialEndYear), parseInt(trialEndMonth)+1, parseInt(trialEndDay)].join('/')
    const confirm = window.confirm(`Really change their trial to end on ${newTrialEndDate}?`)
    if (confirm) {
      const { params: { team_id } } = this.props
      this.teams.updateLicense(team_id, { trial_ends_at: new Date(newTrialEndDate) })
    }
  }

  /*
   * Show the edit trial form for a trialing team
   */
  renderEditTrial() {
    if (!this.isUserAdminOrBroker()) return

    const { params: { team_id } } = this.props
    const team = this.teams.findById(team_id)
    if (team.license && team.license.status.toLowerCase() !== 'trialing') return

    if (this.props.auth.user.admin && team.license && !team.license.stripe_subscription_id) {
      const { trialEndDay, trialEndMonth, trialEndYear } = this.state
      const year = new Date().getFullYear()
      return (
        <div>
          <span>Trial Expires: </span>
          <label>
            <span> Month: </span>
            <select value={trialEndMonth} onChange={e => this.setState({ trialEndMonth: e.target.value})}>
              {range(0, 12).map(m => parseInt(m)).map(month => (
                <option key={month} value={month}>{parseInt(month)+1}</option>
              ))}
            </select>
          </label>
          <label>
            <span> Day: </span>
            <select value={trialEndDay} onChange={e => this.setState({ trialEndDay: e.target.value })}>
              {range(1, 32).map(d => parseInt(d)).map(day => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
          </label>
          <label>
            <span> Year: </span>
            <select value={trialEndYear} onChange={e => this.setState({ trialEndYear: e.target.value })}>
              {range(Math.min(year,trialEndYear), year + 5).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </label>
          {' '}
          <button className="button" onClick={this.handleEditTrial}>Update</button>
        </div>
      )
    }
  }

  handleSaveName = event => {
    event.preventDefault()
    const { params: { team_id } } = this.props
    const team = this.teams.findById(team_id)
    const name = event.target.elements['name'].value
    this.teams.update(Object.assign(team, { name } )).then(() => {
      this.setState({ editingName: false })
    })
  }

  /*
   * Show the link to a given website
   */
  renderWebsiteURLLink(w) {
    if (w.root_url) {
      return <a href={w.root_url}>{w.default_url}</a>
    } else {
      return <Link to={`/websites/${w.id}`}>(not installed)</Link>
    }
  }

  updateOfficeName = event => {
    event.preventDefault()
    const form = event.target
    const officeName = form.elements.namedItem('officeName').value

    const { params: { team_id } } = this.props
    const team = this.teams.findById(team_id)

    // Update the team object with the new office name
    this.teams.update(Object.assign(team, { office_name: officeName })).then(() => {
        this.setState({ editingOfficeName: false })
    })
  }

  renderOfficeName() {
    let contents
    const { params: { team_id } } = this.props
    const team = this.teams.findById(team_id)
    const officeName = team && team.office_name

    if (this.state.editingOfficeName) {
        contents = <form onSubmit={this.updateOfficeName} style={{display: 'inline-flex', alignItems: 'center'}}>
            <input
                type="text"
                name="officeName"
                defaultValue={officeName || ''}
                placeholder="Enter office name"
                style={{marginRight: '5px'}}
            />
            <button style={{margin: 0, marginLeft: '5px'}}>Save</button>
            <a href="#" style={{marginLeft: '5px'}} onClick={event => {
                event.preventDefault()
                this.setState({ editingOfficeName: false })
            }}>Cancel</a>
        </form>
    } else {
        contents = <span>
  {officeName || ''} <a href="#" onClick={event => {
            event.preventDefault()
            this.setState({ editingOfficeName: true })
        }}>Edit</a>
</span>
    }
    return (
        <li><strong>Office Name:</strong> {contents}</li>
    )
  }


  render() {
    const { params: { team_id } } = this.props

    if (!this.teams.loaded) return <div className="loading">Loading...</div>

    const team = this.teams.findById(team_id)

    let name = this.props.auth.user.admin ? this.state.editingName ? (
      <form onSubmit={this.handleSaveName}><span style={{ opacity: .6 }}>Team:</span> <input type="text" name="name" defaultValue={team.name} /> <a href="#" style={{ fontSize: '.7em' }} onClick={event => {
        event.preventDefault()
        this.setState({ editingName: false })
      }}>Cancel</a><button>Save</button></form>
    ) : (
      <span><span style={{opacity: .6}}>Team:</span> {team.name} <a href="#" style={{fontSize: '.7em'}} onClick={event => {
        event.preventDefault()
        this.setState({ editingName: true })
      }}>Edit</a></span>
    ) : (
      <span><span style={{ opacity: .6 }}>Team:</span> {team.name}</span>
    )

    if (team.license && team.license.revoked) {
      name = (
        <span>
          {name} <strong className="team-revoked">REVOKED</strong>
        </span>
      )
    }

    return (
      <div className="container padded team">
        <div className="container">
          <div className="panel full padded">
            <h2 className="title">{name}</h2>

            <div className="container">
              <ul className="half">
                {this.renderResellerTeam()}
                {this.renderPlan()}
                {this.renderLicenseStatus()}
                {this.renderOfficeName()}
              </ul>

              <div className="half">
                <Collapsible triggerClassName="order button" triggerOpenedClassName="order button" trigger="Raw Order Details">
                  <pre>{JSON.stringify(team.order, null, 2)}</pre>
                </Collapsible>
                {this.renderOnboardingSelections()}
              </div>
            </div>

            {this.renderEditTrial()}

            <section>
              {team.customer && team.customer.stripe_customer_id && <a className="button" href={`https://dashboard.stripe.com/customers/${team.customer.stripe_customer_id}`}><Icon icon="cc-stripe"/> Customer</a>}
              {team.license && team.license.stripe_subscription_id  && <a className="button" href={`https://dashboard.stripe.com/subscriptions/${team.license.stripe_subscription_id}`}><Icon icon="cc-stripe"/> Subscription</a>}
            </section>

            {this.renderStartTrial()}
            {this.renderRevokeButton()}
          </div>
        </div>

        <div className="container">
          {this.teams.usersLoaded && <div className="panel half padded">
            <h3>Users:</h3>
            <table className="zebra">
              <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th></th>
              </tr>
              </thead>
              <tbody className="padded">
              {this.teams.users.map(u => (
                <tr key={u.id}>
                  <td>{u.name}{u.admin ? ' [admin]' : null}</td>
                  <td>{u.email}</td>
                  <td className="right"><a className="button" onClick={this.impersonateUser(u)}>Impersonate</a></td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>}

          {this.websites.loaded && <div className="panel half padded">
            <h3>Websites:</h3>
            <table className="zebra">
              <thead>
              <tr>
                <td>Name</td>
                <td>URL</td>
                <td>Status</td>
              </tr>
              </thead>
              <tbody className="padded">
              {this.websites.map(w => (
                <tr key={w.id}>
                  <td><Link to={`/websites/${w.id}`}>{w.name}</Link></td>
                  <td>{this.renderWebsiteURLLink(w)}</td>
                  <td><span className={`website-status-${w.status}`}>{w.status === 'revoked' ? 'archived' : w.status}</span></td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>}

          {this.renderLegacyPaperwork()}
        </div>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// client/components/sections/Teams/Details.js